type Props = {
	values: string[];
	/** How many to render before collapsing the rest into a count. */
	limit?: number;
	/** How many to put on each line of the hover tooltip. */
	perTooltipLine?: number;
	emptyLabel?: string;
};

/**
 * A listing cell for a rolled-up set of values, with the whole set on hover.
 *
 * Both things this renders can be long. The prefix list for Vitodens 100-W is 86 prefixes - 687
 * characters - and its size list is nine values, because those attributes belong to a serial prefix rather
 * than to the appliance. Printing either in full makes the row unreadable and pushes the Actions column
 * off the screen, so the cell shows the first few and the rest are available by hovering.
 *
 * The hover is the native title attribute rather than the shared Tooltip component, which looks better but
 * cannot be used here: it is absolutely positioned, and the DataTable's table element carries
 * overflow-x-auto, which clips it. A native tooltip is drawn by the browser outside the document, so it
 * cannot be clipped by an ancestor - and it honours newlines, which is what makes 86 values readable
 * rather than one unbroken line.
 */
function TruncatedList({
	values,
	limit = 3,
	perTooltipLine = 8,
	emptyLabel = '-',
}: Props) {
	if (!values || values.length === 0) return <>{emptyLabel}</>;

	const shown = values.slice(0, limit);
	const remaining = values.length - shown.length;

	/*
	 * Chunked into lines so the tooltip is a block rather than a single line the width of the screen. Only
	 * worth doing past one line's worth - below that the join adds nothing.
	 */
	const tooltip =
		values.length > perTooltipLine
			? Array.from(
					{ length: Math.ceil(values.length / perTooltipLine) },
					(_, line) =>
						values
							.slice(
								line * perTooltipLine,
								line * perTooltipLine + perTooltipLine
							)
							.join(', ')
			  ).join(',\n')
			: values.join(', ');

	return (
		<span title={tooltip}>
			{shown.join(', ')}
			{remaining > 0 && (
				<span
					className="text-muted"
					style={{ whiteSpace: 'nowrap' }}
				>
					{` +${remaining} more`}
				</span>
			)}
		</span>
	);
}

export default TruncatedList;
